import { Float, FloatProps } from "@headlessui-float/react";
import { Menu, MenuItemProps } from "@headlessui/react";
import { clsx } from "@/utils";
import { Icon } from "../Icon/Icon";
import { Typography } from "../Typography/Typography";
import { Button } from "./Button";
import { getDropdownCaretClass } from "./themes";
import { ButtonProps } from "./types";

export interface ButtonDropdownProps extends ButtonProps {
  position?: FloatProps["placement"];
  offset?: number;
  /** Extra classes for the dropdown panel — e.g. to widen it. */
  menuClassName?: string;
  /** [button label, menu contents]. */
  children: [React.ReactNode, React.ReactNode];
}

/**
 * A split button: the left half fires `onClick`, the caret opens a menu.
 *
 * Visually identical to the Client Manager "Actions" button — both draw the caret from
 * `getDropdownCaretClass`, so the divider, fill and sizing can't drift apart. The difference is
 * behavioural: there the whole button opens the menu (`DropdownMenu` + `Button isDropdown`), whereas
 * here the label half keeps its own action. Use `DropdownMenu` when every option lives in the menu.
 */
export const ButtonDropdown = ({
  className,
  menuClassName,
  children,
  size = "md",
  position = "bottom-end",
  offset = 10,
  disabled,
  ...props
}: ButtonDropdownProps) => (
  <Menu as="div" className={clsx("relative inline-flex", className)}>
    <Float
      offset={offset}
      placement={position}
      enter="duration-100 ease-out"
      enterFrom="opacity-0 scale-50"
      enterTo="opacity-100 scale-100"
      leave="duration-100 ease-in"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-50"
      strategy="fixed"
      shift
      flip
      portal
      tailwindcssOriginClass
    >
      <div className="flex w-full">
        <Button {...props} size={size} disabled={disabled} className="grow rounded-r-none">
          {children[0]}
        </Button>
        <Menu.Button disabled={disabled} aria-label="More options" className={clsx(getDropdownCaretClass(size, disabled))}>
          <Icon name="arrow-down" isCustom size="sm" className={clsx(disabled ? "text-neutral-dark-gray" : "text-white")} />
        </Menu.Button>
      </div>
      <Menu.Items className={clsx("rounded-md border bg-white py-2.5 text-base shadow-lg focus:outline-none", menuClassName)}>
        {children[1]}
      </Menu.Items>
    </Float>
  </Menu>
);

const ButtonDropdownMenuItem = ({ className, children, ...props }: MenuItemProps<"li">) => (
  <Menu.Item
    as="li"
    className={clsx("flex w-56 cursor-pointer items-center px-3 py-3 hover:bg-primary-light hover:text-primary", className?.toString())}
    {...props}
  >
    {children}
  </Menu.Item>
);

/** A labelled group divider inside the menu — e.g. "Bulk Upload Options". Matches ClientsActionMenu. */
const ButtonDropdownSection = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={clsx("mt-1 border-t border-neutral-gray px-3 pb-1 pt-2", className)}>
    <Typography variant="small" className="text-neutral-silver-gray">
      {children}
    </Typography>
  </div>
);

ButtonDropdown.Item = ButtonDropdownMenuItem;
ButtonDropdown.Section = ButtonDropdownSection;
